import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TealiumContext } from "./TealiumContext.js";
import { TealiumTrackType } from "../types/TealiumTrackType.js";
import { applyMiddlewares } from "../utils/applyMiddlewares.js";
import { createLogger } from "../utils/debug.js";
import { jsx as _jsx } from "react/jsx-runtime";
const log = createLogger('track');

// See https://docs.tealium.com/platforms/javascript/single-page-applications/
if (typeof window !== 'undefined') {
  window.utag_cfg_ovrd = {
    noview: true
  };
}

/**
 * Checks if the track function of utag,js is present and therefore ready to use.
 */
const isUTagPresent = () => typeof window?.utag?.track === 'function';
const TYPE = 0; // track() argument type position
const DATA = 1; // track() argument data position

export const TealiumProvider = ({
  children,
  presets: {
    website
  },
  middlewares,
  tealiumLoaded = false,
  ...params
}) => {
  const {
    enable = true
  } = params;

  // Pending track events that are dispatched to tealium when ready.
  const queue = useRef([]);

  // If true, the event queue is flushed.
  const [ready, setReady] = useState(tealiumLoaded);

  // Preserves the latest ready value.
  const readyRef = useRef(tealiumLoaded);
  const track = useCallback((...args) => {
    if (!enable) {
      return;
    }
    let trackArgs = args.length === 1 ? args[0] : args;
    if (middlewares) {
      trackArgs = applyMiddlewares(middlewares, trackArgs);
    }

    // When view payloads are tracked, enrich the payload
    // with the required website layer data.
    if (trackArgs[TYPE] === TealiumTrackType.VIEW) {
      trackArgs[DATA] = {
        ...trackArgs[DATA],
        ...website
      };
    }
    const logTrack = state => log(state, `type=${trackArgs[TYPE]}`, trackArgs[DATA]);
    if (readyRef.current && isUTagPresent()) {
      logTrack('sending');
      window.utag.track(...trackArgs);
    } else {
      logTrack('queuing');
      queue.current = [...queue.current, trackArgs];
    }
  }, [enable, middlewares, website]);

  // process queue entries
  useEffect(() => {
    if (ready && ready !== readyRef.current) {
      // only flush queue, if the ready state is true and has changed.
      readyRef.current = ready;
      // copy queue
      const elements = queue.current;
      // clear queue
      queue.current = [];
      log(`flushing tealium track queue (count=${elements.length})`);
      // flush queue
      elements.forEach(args => {
        track(args);
      });
    }
  }, [ready, track]);

  /**
   * When tealium (utag.js) was successfully loaded, mark tealium as ready to use.
   */
  const setTealiumLoaded = useCallback(() => {
    if (isUTagPresent()) {
      log('tealium loaded');
      setReady(true);
    } else {
      log('tealium loaded', new Error(`The window.utag.track() function could not be found.`));
    }
  }, []);
  return /*#__PURE__*/_jsx(TealiumContext.Provider, {
    value: useMemo(() => ({
      setTealiumLoaded,
      track
    }), [setTealiumLoaded, track]),
    children: children
  });
};